import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";

export default function StormDamage() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.saskroofingcompany.com/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Storm & Hail Damage",
          item: "https://www.saskroofingcompany.com/services/storm-damage",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://www.saskroofingcompany.com/services/storm-damage#service",
      serviceType: "Storm Damage Roof Repair",
      name: "Storm & Hail Damage Repair",
      description:
        "Expert hail damage roof repair in Saskatchewan. We provide fast emergency response and insurance claim assistance.",
      url: "https://www.saskroofingcompany.com/services/storm-damage",
      provider: {
        "@id": "https://www.saskroofingcompany.com/#localbusiness",
        "@type": "RoofingContractor",
        name: "Sask Roofing Company",
        url: "https://www.saskroofingcompany.com/",
        telephone: "+1-639-739-7969",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "CAD",
        lowPrice: "500",
        highPrice: "15000",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.saskroofingcompany.com/contact",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-639-739-7969",
          contactType: "customer service",
          areaServed: "CA-SK",
          availableLanguage: "English",
        },
      },
      termsOfService: "https://www.saskroofingcompany.com/about#warranty",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Does insurance cover hail damage on my roof?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, standard homeowner's insurance policies typically cover roof replacement if the damage was caused by a severe hail storm. We can help provide the documentation your adjuster needs.",
          },
        },
        {
          "@type": "Question",
          name: "How do I know if my roof has hail damage?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Look for dented gutters, damaged vents, or bruised shingles (dark spots where granules are missing). However, the best way to know is to have a professional inspection.",
          },
        },
        {
          "@type": "Question",
          name: "Should I call my insurance company or a roofer first?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We recommend calling us for a free inspection first. If we don't find enough damage to warrant a claim, you avoid having a zero-pay claim on your insurance record.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Hail Damage Roof Repair Saskatchewan | Storm Restoration"
        description="Fast storm and hail damage roof repair in Saskatchewan. We offer emergency tarping, thorough inspections, and assistance with your insurance claim."
        schema={schema}
      />

      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a06f0735-dc69-4346-b3b4-960c40ceb8f1.png&output=webp&q=60"
            alt="Storm Damage Repair"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Hail & Storm Damage Roof Repair Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              When severe prairie storms strike, we respond quickly. We provide
              expert storm damage restoration, emergency tarping, and
              comprehensive inspections to support your insurance claim.
            </p>
            <div className="pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link to="/contact">Request a Post-Storm Inspection</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need Storm Damage Restoration
              </h2>
              <p className="text-lg text-muted-foreground">
                You need storm damage repair after a severe weather event,
                particularly between May and August when hail activity peaks in
                Saskatchewan. If your neighborhood was hit by high winds or
                golf-ball-sized hail, your roof likely sustained damage even if
                it isn't leaking yet.
              </p>
              <h3 className="text-2xl font-bold pt-4">How It Works</h3>
              <p className="text-lg text-muted-foreground">
                We start with a comprehensive, photo-documented inspection. If
                the damage is severe, we provide emergency tarping to prevent
                water ingress. Then, we work with you (and your insurance
                adjuster) to provide an accurate scope of work for the necessary
                repairs or full replacement.
              </p>
            </div>
            <div className="bg-muted/30 p-8 rounded-xl border">
              <h3 className="text-2xl font-bold mb-6">
                Why It Suits Saskatchewan
              </h3>
              <ul className="space-y-4">
                {[
                  "Local crews who understand Saskatchewan's unique storm patterns.",
                  "We don't 'storm chase'—we are here year-round to honor our warranties.",
                  "Expertise in identifying hard-to-see hail bruising that adjusters might miss.",
                  "Upgrades to Class 4 impact-resistant shingles available during restoration.",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 bg-muted/30">
        <div className="container px-4 max-w-4xl">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">
              Materials We Use for Storm Restoration
            </h2>
            <p className="text-lg text-muted-foreground">
              When restoring a storm-damaged roof, our goal is to build it back
              stronger. We highly recommend upgrading to Class 3 or Class 4
              impact-resistant architectural shingles, which are specifically
              tested to withstand future hail impacts without fracturing. We
              also use premium synthetic underlayment that provides a superior
              secondary water barrier, and high-wind starters and ridge caps
              designed to resist the extreme uplift forces of prairie gales. For
              emergency tarping, we use heavy-duty, woven polyethylene tarps
              secured with furring strips to guarantee temporary watertightness.
            </p>
            <div className="mt-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a06f0735-dc69-4346-b3b4-960c40ceb8f1.png&output=webp&q=60"
                alt="Roofer installing an emergency tarp on a storm-damaged roof"
                className="rounded-xl shadow-md w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Cost & Process */}
      <section className="py-20 bg-muted/50">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>
              <div className="bg-white p-6 rounded-xl border border-primary/20 shadow-sm mb-6">
                <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2">
                  Estimated Investment
                </div>
                <div className="text-3xl font-bold text-foreground">
                  Your Deductible
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  *If covered by insurance. Out-of-pocket repairs vary ($400 -
                  $15,000+).
                </p>
              </div>
              <p className="text-lg text-muted-foreground">
                If the damage is covered by insurance, your out-of-pocket cost
                is typically just your deductible (often $1,000 to $2,000). If
                you are paying out of pocket, minor wind damage repairs range
                from $400 to $1,000, while severe hail damage usually requires a
                full{" "}
                <Link
                  to="/services/replacement"
                  className="underline text-primary"
                >
                  roof replacement
                </Link>{" "}
                ($6,000 to $15,000). You can also refer to our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                .
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a06f0735-dc69-4346-b3b4-960c40ceb8f1.png&output=webp&q=60"
                  alt="Completed storm damage restoration roof"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">Our Process</h2>
              <div className="space-y-6 mt-8">
                {[
                  {
                    title: "Emergency Response",
                    desc: "Tarping exposed areas to prevent interior damage.",
                  },
                  {
                    title: "Detailed Inspection",
                    desc: "Documenting hail hits, wind uplift, and collateral damage.",
                  },
                  {
                    title: "Claim Assistance",
                    desc: "Providing reports and estimates for your insurance company.",
                  },
                  {
                    title: "Restoration",
                    desc: "Performing high-quality repairs or replacement once approved.",
                  },
                  {
                    title: "Final Walkthrough",
                    desc: "Ensuring your property is fully restored and clean.",
                  },
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection
        service="Storm Damage Repair"
        serviceSlug="storm-damage"
      />

      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Storm Damage FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                Does insurance cover hail damage on my roof?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, standard homeowner's insurance policies typically cover
                roof replacement if the damage was caused by a severe hail
                storm. We can help provide the documentation your adjuster
                needs.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                How do I know if my roof has hail damage?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Look for dented gutters, damaged vents, or bruised shingles
                (dark spots where granules are missing). However, the best way
                to know is to have a professional inspection.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Should I call my insurance company or a roofer first?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                We recommend calling us for a free inspection first. If we don't
                find enough damage to warrant a claim, you avoid having a
                zero-pay claim on your insurance record.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">Hit By a Recent Storm?</h2>
          <p className="text-xl text-primary-foreground/80">
            We serve Saskatoon, Regina, Moose Jaw, Prince Albert, Swift Current,
            Yorkton, Estevan, and Weyburn. We handle emergency tarping quickly
            and most restorations take 1-2 days once approved. Book a free
            inspection to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Get a Free Storm Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/process">See Our Process</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
